import {
  Check,
  CheckCircle2,
  MapPin,
  CalendarDays,
  Clock,
  Plus,
  X,
  Upload,
} from "lucide-react";
import { Slider } from "../ui/slider";
import { PROJECT_TYPES, DAY_OPTIONS, HOUR_OPTIONS } from "./constants";
import { TextField, SelectField } from "./fields";

export function StepIndicator({ step, total }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => {
        const active = i + 1 === step;
        const done = i + 1 < step;
        return (
          <div key={`wizard-step-${i + 1}`} className="flex items-center gap-2 flex-1">
            <div
              className={`h-1 flex-1 rounded-full transition-colors ${
                done || active ? "bg-[#FF5A00]" : "bg-white/10"
              }`}
            />
          </div>
        );
      })}
      <span className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 ml-2 shrink-0">
        Étape {step} / {total}
      </span>
    </div>
  );
}

export function StepProjectType({ value, onChange }) {
  return (
    <div>
      <h3 className="font-display text-2xl md:text-3xl text-white mb-1.5">
        Quel est votre projet ?
      </h3>
      <p className="text-neutral-400 text-sm mb-6">
        Sélectionnez le type de travaux qui vous intéresse.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {PROJECT_TYPES.map((p) => {
          const active = value === p.key;
          const Icon = p.Icon;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => onChange(p.key)}
              data-testid={`project-type-${p.key}`}
              className={`text-left p-5 rounded-2xl border transition-all ${
                active
                  ? "border-[#FF5A00] bg-[#FF5A00]/10 shadow-lg shadow-[#FF5A00]/10"
                  : "border-white/10 bg-white/[0.02] hover:border-white/25"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    active ? "bg-[#FF5A00] text-white" : "bg-white/[0.05] text-neutral-300"
                  }`}
                >
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base text-white mb-0.5 leading-tight">
                    {p.label}
                  </div>
                  <div className="text-xs text-neutral-400 leading-relaxed">
                    {p.desc}
                  </div>
                </div>
                {active && (
                  <Check size={16} className="text-[#FF5A00] shrink-0 mt-1" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function StepSurface({ value, onChange }) {
  return (
    <div>
      <h3 className="font-display text-2xl md:text-3xl text-white mb-1.5">
        Surface estimée
      </h3>
      <p className="text-neutral-400 text-sm mb-8">
        Faites glisser le curseur pour indiquer la surface approximative de
        façade à traiter.
      </p>
      <div className="text-center mb-6">
        <span className="text-neutral-400 text-sm mr-2">Surface estimée :</span>
        <span
          data-testid="contact-surface-value"
          className="font-display text-3xl md:text-4xl text-[#FF5A00]"
        >
          {value[0]} m²
        </span>
      </div>
      <Slider
        value={value}
        onValueChange={onChange}
        min={20}
        max={500}
        step={5}
        data-testid="contact-surface-slider"
        className="my-4"
      />
      <div className="flex justify-between text-[10px] text-neutral-500 mt-2">
        <span>20 m²</span>
        <span>500 m²</span>
      </div>
    </div>
  );
}

export function StepDescription({ message, onMessage, photos, onAddPhotos, onRemovePhoto, fileRef }) {
  return (
    <div>
      <h3 className="font-display text-2xl md:text-3xl text-white mb-1.5">
        Décrivez votre projet
      </h3>
      <p className="text-neutral-400 text-sm mb-6">
        Racontez-nous ce que vous souhaitez faire. Ajoutez éventuellement des
        photos pour nous aider à mieux comprendre.
      </p>
      <label className="block mb-5">
        <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-400 mb-1.5">
          Description
        </span>
        <textarea
          rows={5}
          value={message}
          onChange={(e) => onMessage(e.target.value)}
          placeholder="Ravalement complet, reprise de fissures, nettoyage, imitation pierre autour des fenêtres…"
          data-testid="contact-message"
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A00]/60 focus:ring-2 focus:ring-[#FF5A00]/20 transition resize-none"
        />
      </label>

      <div>
        <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-400 mb-2">
          Photos (facultatif) · jusqu&apos;à 6
        </span>
        <div className="flex flex-wrap gap-2">
          {photos.map((p, idx) => (
            <div
              key={p.id}
              className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/10 group"
            >
              <img
                src={p.dataUrl}
                alt={p.name}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => onRemovePhoto(idx)}
                data-testid={`contact-photo-remove-${idx}`}
                className="absolute inset-0 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={14} className="text-white" />
              </button>
            </div>
          ))}
          {photos.length < 6 && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="contact-photos-add"
              className="w-16 h-16 rounded-lg border border-dashed border-white/20 flex items-center justify-center text-neutral-400 hover:text-white hover:border-white/40 transition"
            >
              <Upload size={16} />
            </button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          hidden
          accept="image/*"
          multiple
          onChange={(e) => onAddPhotos(e.target.files)}
          data-testid="contact-photos-input"
        />
      </div>
    </div>
  );
}

export function StepSlots({ slots, onUpdate, onAdd, onRemove }) {
  return (
    <div>
      <h3 className="font-display text-2xl md:text-3xl text-white mb-1.5">
        Vos disponibilités
      </h3>
      <p className="text-neutral-400 text-sm mb-6">
        Proposez un ou plusieurs créneaux qui vous arrangent pour la visite
        technique. GMC vous confirmera par téléphone.
      </p>

      <div className="space-y-3">
        {slots.map((s, idx) => (
          <div
            key={`slot-${idx}`}
            className="flex items-center gap-2 md:gap-3"
            data-testid={`slot-row-${idx}`}
          >
            <span className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 w-16 shrink-0">
              Créneau {idx + 1}
            </span>
            <div className="flex-1 grid grid-cols-2 gap-2">
              <SelectField
                Icon={CalendarDays}
                value={s.day}
                onChange={(v) => onUpdate(idx, "day", v)}
                options={DAY_OPTIONS}
                placeholder="Jour"
                testId={`slot-day-${idx}`}
              />
              <SelectField
                Icon={Clock}
                value={s.hour}
                onChange={(v) => onUpdate(idx, "hour", v)}
                options={HOUR_OPTIONS}
                placeholder="Heure"
                testId={`slot-hour-${idx}`}
              />
            </div>
            <button
              type="button"
              onClick={() => onRemove(idx)}
              disabled={slots.length === 1}
              data-testid={`slot-remove-${idx}`}
              className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center text-neutral-400 hover:text-white hover:border-white/25 transition disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Supprimer ce créneau"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onAdd}
        data-testid="slot-add"
        className="mt-4 inline-flex items-center gap-2 text-sm text-[#FF5A00] hover:underline"
      >
        <Plus size={14} /> Ajouter un autre créneau
      </button>
    </div>
  );
}

export function StepPersonal({ data, update }) {
  return (
    <div>
      <h3 className="font-display text-2xl md:text-3xl text-white mb-1.5">
        Vos coordonnées
      </h3>
      <p className="text-neutral-400 text-sm mb-6">
        Pour vous rappeler et confirmer le rendez-vous.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <TextField
          label="Prénom"
          value={data.prenom}
          onChange={update("prenom")}
          testId="contact-prenom"
          autoComplete="given-name"
          required
        />
        <TextField
          label="Nom"
          value={data.nom}
          onChange={update("nom")}
          testId="contact-nom"
          autoComplete="family-name"
          required
        />
        <TextField
          label="Téléphone"
          type="tel"
          value={data.phone}
          onChange={update("phone")}
          testId="contact-phone"
          autoComplete="tel"
          required
        />
        <TextField
          label="Adresse mail"
          type="email"
          value={data.email}
          onChange={update("email")}
          testId="contact-email"
          autoComplete="email"
          required
        />
      </div>

      <div className="mt-3">
        <TextField
          label="Adresse du chantier"
          value={data.address}
          onChange={update("address")}
          testId="contact-address"
          icon={MapPin}
          autoComplete="street-address"
          placeholder="12 rue de Lille"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <TextField
          label="Ville"
          value={data.city}
          onChange={update("city")}
          testId="contact-city"
          autoComplete="address-level2"
          placeholder="Valenciennes"
          required
        />
        <TextField
          label="Code postal"
          value={data.zipCode}
          onChange={update("zipCode")}
          testId="contact-zipcode"
          autoComplete="postal-code"
          placeholder="59300"
          required
        />
      </div>
    </div>
  );
}

export function SuccessCard({ onReset }) {
  return (
    <div
      data-testid="contact-success"
      className="glass-strong rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF5A00]/15 blur-3xl pointer-events-none"
      />
      <div className="relative">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF5A00]/15 mb-5">
          <CheckCircle2 size={32} className="text-[#FF5A00]" />
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-white mb-3 leading-tight">
          Votre demande de rendez-vous a bien été envoyée
        </h3>
        <p className="text-neutral-300 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
          L&apos;équipe GMC vous recontacte rapidement pour confirmer le
          créneau.
        </p>
        <p className="text-sm text-neutral-500 mt-4">
          Une urgence ? Appelez-nous directement au{" "}
          <a
            href="tel:+33763433867"
            className="text-[#FF5A00] hover:underline"
          >
            07 63 43 38 67
          </a>
          .
        </p>
        <button
          type="button"
          onClick={onReset}
          data-testid="contact-new-request"
          className="mt-6 text-sm text-neutral-400 hover:text-white underline underline-offset-4"
        >
          Faire une nouvelle demande
        </button>
      </div>
    </div>
  );
}
